import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { roleToggler } from "../../../redux-toolkit/features/access/accessSlice";
import DateRangePickerComp from "../../UI/DateRangePicker";
import "./AdminDashboard.css";
import { BadgesSection } from "./BadgesSection/BadgesSection";
import { Graph } from "./Charts/Graph";
import { QuestActivity } from "./Quest Activity/QuestActivity";
import {
  useBadgeStatsQuery,
  useBookDataQuery,
  useUserStatsQuery,
  useBookStatsQuery,
} from "../../../redux-toolkit/features/dashboard/dashboardApliSlice";
import ProgressBar from "../../UI/ProgressBar";
import { setActiveItem, setSelectBooks } from '../../../redux-toolkit/features/theme/themeSlice';
import { selectSelectedCompany } from '../../../redux-toolkit/features/auth/authSlice';


export const AdminDashboard = () => {
  const dispatch = useDispatch();

  React.useEffect(() => {
    dispatch(roleToggler('Admin'));
    dispatch(setActiveItem(1));
  }, [dispatch]);

  const { companyId } = useSelector((state) => state.auth.user);
  const selectedCompany = useSelector(selectSelectedCompany);
  const [dateRange, setDateRange] = React.useState(null);
  const selectBooks = useSelector((state) => state.theme.selectBooks);
  
  // Use selectedCompany if available, otherwise fallback to companyId from user object
  const activeCompanyId = selectedCompany || companyId;


  // Organizationals
  const { data: bookOrg, isLoading } = useBookDataQuery(
    {
      userType: "admin",
      type: 'org',
      companyId: activeCompanyId,
      selectedCompany: activeCompanyId,
    },
    { refetchOnMountOrArgChange: true }
  );

  // Company
  const { data: bookCompany, isLoading: companyLoading, refetch: refetchBookCompany } = useBookDataQuery(
    {
      userType: "admin",
      type:'company',
      companyId: activeCompanyId,
      selectedCompany: activeCompanyId,
    },
    { refetchOnMountOrArgChange: true }
  );

  const { data: badgeStatsCompany, isLoading: badgeStatsCompanyLoading, refetch: refetchBadgeStats } =
    useBadgeStatsQuery(
      {
        type: 'company',
        companyId: activeCompanyId,
        selectedCompany: activeCompanyId,
        startDate: dateRange ? dateRange[0] : undefined,
        endDate: dateRange ? dateRange[1] : undefined,
      },
      { refetchOnMountOrArgChange: true }
    );
  const { data: userStatsCompany, refetch: refetchUserStats } = useUserStatsQuery(
    {
      type: 'company',
      companyId: activeCompanyId,
      selectedCompany: activeCompanyId,
      startDate: dateRange ? dateRange[0] : undefined,
      endDate: dateRange ? dateRange[1] : undefined,
    },
    { refetchOnMountOrArgChange: true }
  );

  const companyBooks = bookCompany?.data || [];
  const orgBooks = bookOrg?.data || [];
  const availableBooks = [...companyBooks, ...orgBooks];

  React.useEffect(() => {
    if (!availableBooks.length) return;

    const hasSelectedBook =
      !!selectBooks?._id &&
      availableBooks.some((book) => book?._id === selectBooks._id);

    // Set default only when no valid selection exists.
    if (!hasSelectedBook) {
      dispatch(
        setSelectBooks({
          _id: availableBooks[0]._id,
          name: availableBooks[0]?.name,
          type: availableBooks[0]?.type,
        })
      );
    }
  }, [bookCompany?.data, bookOrg?.data, selectBooks?._id, dispatch]);

  const bookIdForStats = selectBooks?._id || availableBooks[0]?._id;
  const selectedBookForStats = availableBooks.find(
    (book) => book?._id === bookIdForStats
  );
  const isOrgBook =
    String(selectedBookForStats?.type || selectBooks?.type || "").toUpperCase() ===
      "ORG" || orgBooks.some((book) => book?._id === bookIdForStats);
  const bookStatsType = isOrgBook ? "org" : "company";
  const shouldSkipBookStats =
    !activeCompanyId || !bookIdForStats || !selectedBookForStats;
  
  const { data: bookStatsCompany, isFetching: bookStatsCompanyFetching, refetch: refetchBookStats } = useBookStatsQuery(
    {
      bookId: bookIdForStats,
      type: bookStatsType,
      userType: "admin",
      companyId: activeCompanyId,
      selectedCompany: activeCompanyId,
      startDate: dateRange ? dateRange[0] : undefined,
      endDate: dateRange ? dateRange[1] : undefined,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: shouldSkipBookStats, // Skip if no book ID available
      keepPreviousData: true,
    }
  );

  // Refetch all queries when selectedCompany changes
  React.useEffect(() => {
    if (selectedCompany && activeCompanyId) {
      // Only refetch queries that are not skipped
      if (refetchBookCompany) refetchBookCompany();
      if (refetchBadgeStats) refetchBadgeStats();
      if (refetchUserStats) refetchUserStats();
      // Only refetch bookStats if we have a bookId and the query is not skipped
      if (bookIdForStats && !shouldSkipBookStats && refetchBookStats) {
        refetchBookStats();
      }
    }
  }, [selectedCompany, activeCompanyId, bookIdForStats, shouldSkipBookStats, refetchBookCompany, refetchBadgeStats, refetchUserStats, refetchBookStats]);

  if (
    badgeStatsCompanyLoading ||
    companyLoading ||
    isLoading
  ) {
    return <ProgressBar />;
  }

  return (
    <div className="container">
      <div className="dashboard__headers">
        <h4 className="ftwt__b" style={{ color: "var(--pblue)" }}>Company</h4>
        <div className="dashboard__actions">
          <DateRangePickerComp
            onChange={(value) => {
              if (!value) setDateRange(null);
              else
                setDateRange(
                  value.map((val) => new Date(val).toISOString())
                );
            }}
          />
        </div>
      </div>
      <div className="seprator"></div>
      <div className="adminLayout">
        <div className="dashboard__content ">
          <Graph
            badgeStats={badgeStatsCompany}
            userStats={userStatsCompany}
            bookStats={bookStatsCompany}
            bookData={[...(bookCompany?.data || []),...(bookOrg?.data || [])]}
            isBookStatsRefreshing={
              !!bookIdForStats && bookStatsCompanyFetching
            }
          />
          <QuestActivity
            bookData={bookOrg}
            companyBookData={bookCompany}
            selectedCompany={activeCompanyId}
          />
          <BadgesSection selectedCompany={activeCompanyId} />
        </div>
      </div>
    </div>
  );
};
